import MaldivesBonusInclusionsCampaignLabel, { hasMaldivesBonusInclusionsCampaignTag } from 'components/Common/Labels/MaldivesBonusInclusionsCampaignLabel'
import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import OfferCardFacilitiesFallbackContext from 'contexts/OfferList/OfferCardFacilitiesFallbackContext'
import OfferCardFacilitiesFallback from './OfferCardFacilitiesFallback'
import type { ComponentProps } from 'react'
import React, { useContext, useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import Caption from 'LuxKit/Typography/Caption'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import LEOfferPriceDetails, { type LEOfferPricingComponent } from './LEOfferPriceDetails'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusHiddenOfferCarouselCard from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferCarouselCard'
import LuxPlusHiddenOfferCarouselCardMedium from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferCarouselCardMedium'
import { connect } from 'react-redux'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { EmptyObject } from 'lib/object/objectUtils'
import { getShouldUseDatedStreamPrices } from 'lib/search/searchUtils'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import isHotelFlightBundleOffer from 'lib/offer/isHotelFlightBundleOffer'
import { isFlashHotelOffer, isHotelPackage } from 'lib/offer/offerTypes'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import Group from 'components/utils/Group'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyStayUpgradeBenefitPill from 'loyalty/components/LoyaltyStayUpgradeBenefitPill'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import GeoContext from 'contexts/geoContext'
import ContentTagLabels from '../../Content/ContentTagLabels'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'
import OfferCarouselMarketingHeadlineContext from 'contexts/OfferList/OfferCarouselMarketingHeadlineContext'
import { formatNearbyPlaceDistance } from 'components/OfferPage/NavigableOfferContent/formatNearbyPlaceDistance'
import GrowthCarouselCardSmall from './GrowthOfferCard/GrowthCarouselCardSmall'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import { getGrowthOfferHighlights } from 'lib/offer/growthOfferTile'
import useGrowthOfferTile from './GrowthOfferCard/useGrowthOfferTile'
import GrowthInclusionList from './GrowthOfferCard/GrowthInclusionList'
import { useOfferCardPriceOverride } from 'contexts/OfferList/OfferCardPriceOverrideContext'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'

interface MappedProps {
  isLuxPlusEnabled: boolean
  isLuxPlusHiddenOffer: boolean
  inclusionsViewableLuxPlusTier: App.MembershipTier
}

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends MappedProps, InheritedCarouselCardMediumProps {
  offer: App.HotelOffer | App.HotelOfferSummary
  onClick?: (isRightClick?: boolean) => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filters?: App.OfferListFilters
  distance?: number
  distanceFrom?: string
  PricingComponent?: LEOfferPricingComponent
  CtaComponent?: React.ComponentType
}

const DISTANCE_SEPARATOR = '·'

function formatDistanceAway(intl: IntlShape, distanceMeters: number, regionCode: string) {
  return intl.formatMessage(
    {
      id: 'dGThnkp0-f8yzsy5SnOV',
      defaultMessage: '{distance} away',
      description: 'Distance away label shown beside the offer location on nearby recommendation cards',
    },
    {
      distance: formatNearbyPlaceDistance(intl, distanceMeters, regionCode),
    },
  )
}

function getLocationWithDistance(
  intl: IntlShape,
  location: string,
  distanceMeters: number | undefined,
  distanceFrom: string | undefined,
  regionCode: string,
) {
  if (distanceMeters === undefined || !distanceFrom) {
    return location
  }

  const distanceLabel = formatDistanceAway(intl, distanceMeters, regionCode)
  return <Group direction="horizontal" verticalAlign="center" gap={4}>
    <Caption colour="normal-contrast" variant="large" lineClamp={1}>
      {location}
    </Caption>
    <Caption colour="light-foreground-alt" variant="medium">
      {DISTANCE_SEPARATOR}
    </Caption>
    <Caption colour="light-foreground-alt" variant="medium" lineClamp={1}>
      {distanceLabel}
    </Caption>
  </Group>
}

function LEOfferCard(props: Props) {
  const isGrowthTile = useGrowthOfferTile()
  const intl = useIntl()
  const {
    offer,
    size,
    onClick,
    className,
    offerUrl,
    isLuxPlusHiddenOffer,
    isLuxPlusEnabled,
    selectedDatesAndRoomsFilter,
    filters,
    inclusionsViewableLuxPlusTier,
    variant,
    eagerLoadImage,
    distance,
    distanceFrom,
    PricingComponent,
    CtaComponent,
  } = props
  const showMarketingHeadline = useContext(OfferCarouselMarketingHeadlineContext)
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()
  const { lowestPricePackage } = offer
  const isUltraLux = isUltraLuxOffer(offer)
  const memberPrice = lowestPricePackage?.memberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (lowestPricePackage?.price ?? 0) - memberPrice : undefined
  const luxPlusInclusions = lowestPricePackage?.luxPlusInclusionsByTier?.[inclusionsViewableLuxPlusTier as App.MembershipSubscriptionTier]
  const offerLabels = useMemo(() => offer.urgencyTags.filter(label => label.type !== 'left'), [offer])

  const isFlashHotel = isFlashHotelOffer(offer)
  const facilitiesFallbackEnabled = useContext(OfferCardFacilitiesFallbackContext)
  const shouldShowFlashInclusions = isFlashHotel

  const usesDatedStreamPrices = getShouldUseDatedStreamPrices(filters)
  const priceOverride = useOfferCardPriceOverride(offer)
  const bestPriceFilters = usesDatedStreamPrices || !!priceOverride ? EmptyObject : (selectedDatesAndRoomsFilter ?? EmptyObject)
  const bestPkg = useOffersBestPrices(offer, bestPriceFilters)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const inclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.tileInclusions?.length) {
      return offer.tileInclusions
    }
    if (offer.lowestPricePackage?.inclusions?.length) {
      return offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate')
    }
    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    return pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate') || []
  }, [offer])

  const loyaltyInclusions = useLoyaltyBenefitInclusions(offer)
  const loyaltyCount = loyaltyInclusions.length

  const { inclusions: growthInclusions, additionalInclusionCount: growthExcludedInclusionsCount } = useMemo(
    () => getGrowthOfferHighlights([...inclusions, ...loyaltyInclusions]),
    [inclusions, loyaltyInclusions],
  )

  const showLoyaltyInline = inclusions.length < 3

  const shortInclusions = useMemo(() => take(inclusions, 3), [inclusions])

  const { currentRegionCode } = useContext(GeoContext)
  const location = useMemo(() => {
    const location = [offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')
    return getLocationWithDistance(intl, location, distance, distanceFrom, currentRegionCode)
  }, [currentRegionCode, distance, distanceFrom, intl, offer.locationHeading, offer.locationSubheading])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    // Stream quotes do not include a matching package's fee-excluded earn basis.
    // Do not estimate points from an unrelated undated package.
    if (usesDatedStreamPrices || offer.bundledWithFlightsOnly) {
      return []
    }
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        pkg: isHotelPackage(pkg) ? pkg : undefined,
        price: pkg.price,
        memberPrice: pkg.memberPrice,
        currentRegionCode,
        nights: pkg.duration,
      }),
    ]
  }, [offer, pkg, currentRegionCode, usesDatedStreamPrices])

  // Burn price must match what LEOfferPricing actually displays for this card (fee-inclusive),
  // not the fee-excluded basis the earn calculator above intentionally uses.
  const { default: tileBurnPricing, member: tileBurnMemberPricing } = useOfferPackagePrice(pkg, offer)
  const tileBurn = useSearchOfferBurnPricing({
    price: tileBurnPricing.price,
    memberPrice: tileBurnMemberPricing?.price,
    loyaltyProductType: getLoyaltyProductType(offer),
    mx: getHotelEncryptedMargin(offer.type, isHotelPackage(pkg) ? pkg : undefined),
    viewPriceInPoints: !!filters?.viewPriceInPoints,
    disabled: usesDatedStreamPrices || !tileBurnPricing.price || !!offer.bundledWithFlightsOnly,
  })

  const hasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')
  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  const showMaldivesBonusInclusionsCampaignLabel = hasMaldivesBonusInclusionsCampaignTag(offer.campaignTags)

  // Check if this is a charter bundle offer
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])

  const charterBundleImageLabel = isCharterBundleOffer
    ? (
        <NeutralLabel>
          {intl.formatMessage({
            id: 'PjmdhR5JAQXM7v4WOevW',
            defaultMessage: 'Hotel & Flight bundle',
            description: 'Label for charter bundle offers combining hotel and flight',
          })}
        </NeutralLabel>
      )
    : undefined

  let urgencyTagLabel: React.ReactElement | undefined = undefined
  const urgencyTagAlreadyActive = offerLabels.find((ol: App.OfferUrgencyLabel) => ol.type == 'left' && ol.end || ol.type == 'lux_plus_general_release')
  if (offer?.visibilitySchedule?.end && !urgencyTagAlreadyActive) {
    urgencyTagLabel = <OfferUrgencyCountdownLabel
      endDate={offer?.visibilitySchedule?.end}
      tagPlace="offer-card"
      fallback={size === 'medium' && isUltraLux ? <ProductTypeLabel productType={offer.productType} /> : undefined}
    />
  }
  const hasInclusions = shortInclusions.length > 0
  const fallbackFacilities = offer.lowestPricePackage?.roomType.amenities ?? EmptyArray
  const facilitiesElement = facilitiesFallbackEnabled && !isLuxPlusHiddenOffer && !hasInclusions
    ? <OfferCardFacilitiesFallback offerId={offer.id} filters={filters} fallbackFacilities={fallbackFacilities} />
    : undefined
  const remainingInclusionsCount = inclusions.length - shortInclusions.length + (showLoyaltyInline ? 0 : loyaltyCount)
  const showInclusionsInsteadOfTagline = shouldShowFlashInclusions && hasInclusions
  const showMarketingHeadlineWithInclusions = showMarketingHeadline && showInclusionsInsteadOfTagline

  const inclusionsElement = (shouldShowFlashInclusions && hasInclusions) ? <Group
    gap={8}
    tabletGap={8}
    direction="vertical"
    tabletDirection="horizontal"
    horizontalAlign="start"
    wrap="wrap"
    style={{ paddingBottom: 8 }}
  >
    {shortInclusions.map((inclusion) => <OfferInclusionItem
      key={inclusion.id}
      inclusion={inclusion}
      size="medium"
      isHighlighted={false}
    />)}
    {showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="offer-page" />}
    {remainingInclusionsCount > 0 && (
      <BodyText variant="medium" underline>
        {intl.formatMessage(
          {
            id: '-_Y7PlvvzQaDdQJ6J66z',
            defaultMessage: '+{count} more',
            description: 'Text indicating additional inclusions count on offer card',
          },
          { count: remainingInclusionsCount },
        )}
      </BodyText>
    )}
  </Group> : facilitiesElement
  const flashTileDescription = showInclusionsInsteadOfTagline && !showMarketingHeadlineWithInclusions ? undefined : offer.name

  // Belt-and-suspenders for points carousels: never mount Hotel+Flight force-bundle tiles.
  if (filters?.viewPriceInPoints && isHotelFlightBundleOffer(offer)) {
    return null
  }

  // Belt-and-suspenders for "show offers within my balance": never mount a tile priced above the
  // caller's points cap (filters.pointsLte), in case the server-side pointsLte filter lets one
  // through. Only ever set by spend-points carousels, so this is a no-op everywhere else.
  if (
    filters?.pointsLte !== undefined
    && tileBurn.priceInPointsForEarn !== undefined
    && tileBurn.priceInPointsForEarn > filters.pointsLte
  ) {
    return null
  }

  const useMediumHiddenCard = isLuxPlusHiddenOffer && size === 'medium'

  if (isGrowthTile) {
    const growthOfferLabels = offerLabels.filter(label => label.type !== 'lux_plus_general_release' && label.type !== 'popular_option_room_count')
    // Control excludes `left` tags; relocating urgency must not make them eligible again.
    const countdownTag = offerLabels.find(label => label.type === 'lux_plus_general_release' && label.end)
    // Availability is date-specific; never advertise room counts for undated offers.
    const availableRooms = selectedDatesAndRoomsFilter?.checkIn && isHotelPackage(pkg) ? pkg.availableRooms : undefined
    const roomCountLabel = availableRooms && availableRooms > 0 ? <OfferUrgencyRoomCount availableRoomCount={availableRooms} /> : undefined
    const offerCountdown = offer.visibilitySchedule?.end ? <OfferUrgencyCountdownLabel
      endDate={offer.visibilitySchedule.end}
      tagPlace="offer-card"
      fallback={roomCountLabel}
    /> : roomCountLabel
    // Let the existing countdown decide visibility, including expiry and display
    // windows. A hidden early-access timer must not suppress the offer timer.
    const imageUrgencyLabel = countdownTag?.end ? <OfferUrgencyCountdownLabel
      endDate={countdownTag.end}
      tagPlace="lux-plus-offer-label"
      isLuxPlusEarlyAccess
      fallback={offerCountdown}
    /> : offerCountdown

    // Growth keeps its own exclusive heading after the old LTLE experiment releases control.
    const growthInclusionsList = <GrowthInclusionList
      additionalInclusionCount={growthExcludedInclusionsCount}
      heading={isFlashHotel && !isCharterBundleOffer ? <BodyTextBlock variant="small" weight="bold" colour="highlight-primary-dark">
        {intl.formatMessage({
          id: '06msxSXUTAbaLZZ1hUlK',
          defaultMessage: 'Luxury Escapes exclusive offer',
          description: 'Exclusive offer heading shown above inclusions on Growth homepage tiles for flash hotels (excluding charter bundles)',
        })}
      </BodyTextBlock> : undefined}
    >
      {growthInclusions.map(inclusion => <OfferInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        size="small"
        lineClamp={1}
        isHighlighted={false}
      />)}
    </GrowthInclusionList>
    const growthInclusionsElement = facilitiesFallbackEnabled && !isLuxPlusHiddenOffer && growthInclusions.length === 0
      ? <OfferCardFacilitiesFallback offerId={offer.id} filters={filters} fallbackFacilities={fallbackFacilities} size="small">
          {growthInclusionsList}
      </OfferCardFacilitiesFallback>
      : growthInclusionsList

    // Hidden offers keep their existing savings message and unlock flow. Only
    // their presentation and inclusion layout change; never expose hotel identity.
    if (isLuxPlusHiddenOffer) {
      return <ProductPaletteProvider product={offer}>
        <LuxPlusHiddenOfferCarouselCardMedium
          className={className}
          urgencyLabels={offerLabels}
          offer={offer}
          variant={variant}
          eagerLoadImage={eagerLoadImage}
          inclusions={growthInclusionsElement}
          growthTile
          onOfferClick={onClick}
        />
      </ProductPaletteProvider>
    }

    return (
      <ProductPaletteProvider product={offer}>
        <GrowthCarouselCardSmall
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="never" size="small" iconKind={iconKind} iconVariant={iconVariant} />}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={location}
          providerName={offer.property.name}
          title={offer.name}
          priceDetails={<LEOfferPriceDetails variant="default" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} PricingComponent={PricingComponent} CtaComponent={CtaComponent} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          imageCornerLabel={imageUrgencyLabel}
          urgencyLabels={<LabelGroup>
            {showMaldivesBonusInclusionsCampaignLabel && <MaldivesBonusInclusionsCampaignLabel />}
            {charterBundleImageLabel}
            <OfferPartnershipLabel offer={offer} />
            {(hasUpgradablePackages && hasRoomUpgradesAvailable) && <LoyaltyStayUpgradeBenefitPill offer={offer} />}
            <ContentTagLabels offerId={offer.id} page="search" />
            <OfferLabels
              offer={offer}
              urgencyLabels={growthOfferLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          </LabelGroup>}
          taglineInclusions={growthInclusionsElement}
          onClick={onClick}
        />
      </ProductPaletteProvider>
    )
  }

  return (
    <ProductPaletteProvider product={offer}>
      {isLuxPlusHiddenOffer && !useMediumHiddenCard && <LuxPlusHiddenOfferCarouselCard className={className} urgencyLabels={offerLabels} offer={offer} size={size} />}
      {useMediumHiddenCard && <LuxPlusHiddenOfferCarouselCardMedium
        className={className}
        urgencyLabels={offerLabels}
        offer={offer}
        variant={variant}
        eagerLoadImage={eagerLoadImage}
        inclusions={inclusionsElement}
      />}
      {!isLuxPlusHiddenOffer && <>
        {size === 'medium' && <CarouselCardMedium
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={location}
          providerName={offer.property.name}
          title={showInclusionsInsteadOfTagline && !showMarketingHeadlineWithInclusions ? '' : offer.name}
          priceDetails={<LEOfferPriceDetails variant="condensed" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} PricingComponent={PricingComponent} CtaComponent={CtaComponent} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          imageCornerLabel={charterBundleImageLabel}
          urgencyLabels={<LabelGroup>
            {showMaldivesBonusInclusionsCampaignLabel && <MaldivesBonusInclusionsCampaignLabel />}
            <ContentTagLabels offerId={offer.id} page="search" />
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
            {urgencyTagLabel}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
            pointsToApply={tileBurn.pointsToApplyForEarn}
            burnPriceInPoints={tileBurn.priceInPointsForEarn}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          taglineInclusions={inclusionsElement}
          titleVariant={showInclusionsInsteadOfTagline ? 'medium' : 'large'}
          onClick={onClick}
        />}
        {size === 'small' && <CarouselCardSmall
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
          description={flashTileDescription}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={location}
          title={offer.property.name}
          priceDetails={<LEOfferPriceDetails offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} filters={filters} PricingComponent={PricingComponent} CtaComponent={CtaComponent} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          imageCornerLabel={charterBundleImageLabel}
          urgencyLabels={<LabelGroup>
            {showMaldivesBonusInclusionsCampaignLabel && <MaldivesBonusInclusionsCampaignLabel />}
            <ContentTagLabels offerId={offer.id} page="search" />
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {urgencyTagLabel}
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
            pointsToApply={tileBurn.pointsToApplyForEarn}
            burnPriceInPoints={tileBurn.priceInPointsForEarn}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          taglineInclusions={inclusionsElement}
          onClick={onClick}
        />}
      </>}
    </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>): MappedProps {
  return ({
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLuxPlusHiddenOffer: getIsLuxPlusHiddenOffer(state, ownProps.offer),
    inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(state),
  })
}

export default connect(mapStateToProps)(LEOfferCard)
